import { useMemo } from 'react';
import { format } from 'date-fns';
import { Plus } from 'lucide-react';
import BottomSheet from '../ui/BottomSheet';

const COLOR_MAP = {
  blue: '#3b82f6',
  red: '#ef4444',
  green: '#10b981',
  yellow: '#f59e0b',
  purple: '#8b5cf6',
  orange: '#f97316',
  gray: '#6b7280',
};

const GOOGLE_CALENDAR_COLOR = '#4285F4';

function formatEventTime(start, end, allDay) {
  if (allDay) return 'All day';
  const s = new Date(start);
  const e = new Date(end);
  return `${format(s, 'HH:mm')} – ${format(e, 'HH:mm')}`;
}

function eventsOnDate(events, dateStr) {
  const d = new Date(dateStr);
  const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0).getTime();
  const dayEnd = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59).getTime();
  return events.filter((ev) => {
    const start = new Date(ev.start).getTime();
    const end = ev.end ? new Date(ev.end).getTime() : start;
    return start <= dayEnd && end >= dayStart;
  });
}

export default function DayDetailSheet({
  open,
  onOpenChange,
  selectedDate,
  events,
  onAddEvent,
  onEventClick,
}) {
  const dateStr = selectedDate ? selectedDate.slice(0, 10) : null;
  const dayEvents = useMemo(() => {
    if (!dateStr || !events?.length) return [];
    return eventsOnDate(events, dateStr);
  }, [dateStr, events]);

  const formattedDate = dateStr
    ? format(new Date(dateStr + 'T12:00:00'), 'EEEE, d MMM yyyy')
    : '';

  const formattedShort = dateStr ? format(new Date(dateStr + 'T12:00:00'), 'd MMM') : '';

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title={formattedDate}
      maxHeight="max-h-[85vh]"
    >
      <div className="space-y-4">
        <ul className="space-y-2">
          {dayEvents.map((ev) => {
            const isGoogle = ev.extendedProps?.source === 'google';
            const color = isGoogle ? GOOGLE_CALENDAR_COLOR : (COLOR_MAP[ev.extendedProps?.color] || COLOR_MAP.blue);
            const handleClick = () => {
              if (!isGoogle) onEventClick?.(ev.extendedProps);
            };
            return (
              <li key={ev.id}>
                <button
                  type="button"
                  onClick={handleClick}
                  disabled={isGoogle}
                  className={`w-full flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-600 p-3 text-left transition-colors ${
                    isGoogle ? 'cursor-default' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 active:bg-gray-100 dark:active:bg-gray-700'
                  }`}
                >
                  <div
                    className="w-1.5 shrink-0 rounded-full self-stretch min-h-[24px]"
                    style={{ backgroundColor: color }}
                    aria-hidden
                  />
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm font-medium text-gray-900 dark:text-gray-100 truncate ${
                        ev.extendedProps?.is_done ? 'line-through opacity-70' : ''
                      }`}
                    >
                      {ev.title}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatEventTime(ev.start, ev.end, ev.allDay)}
                    </p>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
        {dayEvents.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">No events on this day</p>
        )}
        <button
          type="button"
          onClick={() => onAddEvent?.(dateStr)}
          className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 py-4 text-sm font-medium text-gray-600 dark:text-gray-400 hover:border-blue-400 hover:text-blue-600 dark:hover:border-blue-500 dark:hover:text-blue-400 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Add event on {formattedShort}
        </button>
      </div>
    </BottomSheet>
  );
}
